import React, { useEffect, useMemo, useState } from 'react';
import { api, fileUrl } from '@/lib/api';
import ImageUploader from '@/admin/ImageUploader';
import { Section } from '@/admin/AdminUI';
import { Trash2, ChevronUp, ChevronDown, Plus, Search, X, ShieldAlert } from 'lucide-react';

// Explicit confirmation phrase for the takeover recovery flow. The
// admin must type this string exactly before the takeover button
// becomes enabled. No shortcut, no auto-fill.
const TAKEOVER_CONFIRM_PHRASE = 'TAKE OVER';

export default function GalleryTab() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newImage, setNewImage] = useState('');

  // Round 3 — Homepage picker state.
  // `hg.configured` and `hg.ids` come from GET /admin/settings ->
  // site_settings.homepage_gallery. `configured` is server-owned:
  // once true the notice disappears; there is no client-side toggle
  // to un-configure. `slotPhotos` mirrors the 6 slot preview docs
  // (looked up from `items` OR fetched by ID when a saved photo is
  // outside the currently loaded page).
  const [hg, setHg] = useState({ configured: false, ids: [] });
  const [slotIds, setSlotIds] = useState([null, null, null, null, null, null]);
  const [slotPhotos, setSlotPhotos] = useState({}); // { id: doc }
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSlot, setPickerSlot] = useState(null); // 0..5
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerCursor, setPickerCursor] = useState(null);
  const [pickerHasMore, setPickerHasMore] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [savingSelection, setSavingSelection] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [saveErr, setSaveErr] = useState(null);

  // ===== Admin Recovery UI (concurrency protocol takeover) =====
  // All manual: no auto-polling, no auto-labeling as stale, no
  // auto-takeover, no auto-retry. The admin must explicitly click
  // "Check status", read the details, type the confirmation phrase,
  // and click "Take over". After takeover, status is re-fetched but
  // the admin must retry their save/hide/delete themselves.
  const [recoveryStatus, setRecoveryStatus] = useState(null); // last op-status response
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryErr, setRecoveryErr] = useState(null);
  const [takeoverConfirmText, setTakeoverConfirmText] = useState('');
  const [takeoverBusy, setTakeoverBusy] = useState(false);
  const [takeoverErr, setTakeoverErr] = useState(null);
  const [takeoverMsg, setTakeoverMsg] = useState(null);
  const takeoverCanSubmit = !!(recoveryStatus
    && recoveryStatus.has_pending
    && recoveryStatus.pending_operation
    && takeoverConfirmText === TAKEOVER_CONFIRM_PHRASE
    && !takeoverBusy);

  const load = async () => {
    const r = await api.get('/admin/gallery');
    setItems(r.data || []);
    try {
      const s = await api.get('/admin/settings');
      const stored = (s.data && s.data.homepage_gallery) || { configured: false, ids: [] };
      const ids = Array.isArray(stored.ids) ? stored.ids.slice(0, 6) : [];
      const padded = [0, 1, 2, 3, 4, 5].map((i) => ids[i] || null);
      setHg({ configured: !!stored.configured, ids: [...ids] });
      setSlotIds(padded);
      // Hydrate slot previews. Prefer already-loaded `items`; for any
      // id not in the current admin page, fetch by picker filter.
      const byId = {};
      (r.data || []).forEach((it) => { byId[it.id] = it; });
      const missing = ids.filter((id) => id && !byId[id]);
      if (missing.length > 0) {
        // Look up each missing id by paging the picker until found or
        // exhausted. This is only used when a selected photo lives
        // outside the first 200 admin-list results (production edge).
        const fetched = await fetchByIds(missing);
        fetched.forEach((d) => { byId[d.id] = d; });
      }
      setSlotPhotos(byId);
    } catch (e) {
      // Settings load failure should not break the gallery admin.
      // Leave hg defaults untouched.
    }
  };
  useEffect(() => { load(); }, []);

  // Slot photo lookup — check current admin `items` first, then
  // `slotPhotos` (which may hold docs fetched from the picker endpoint
  // for IDs outside the admin GET's first-200 window).
  const lookupPhoto = (id) => {
    if (!id) return null;
    const inItems = items.find((it) => it.id === id);
    if (inItems) return inItems;
    return slotPhotos[id] || null;
  };

  // Fetch specific IDs by traversing the picker endpoint. This is a
  // rare code path (only when a saved id is outside the admin GET
  // cap). Not used during normal picker browsing.
  const fetchByIds = async (targetIds) => {
    if (!targetIds || targetIds.length === 0) return [];
    const remaining = new Set(targetIds);
    const found = [];
    let cursor = null;
    let safety = 40; // upper bound on pages to prevent runaway
    while (remaining.size > 0 && safety > 0) {
      const r = await api.get('/admin/gallery/picker', { params: { limit: 100, cursor: cursor || undefined } });
      const list = (r.data && r.data.items) || [];
      list.forEach((d) => {
        if (remaining.has(d.id)) {
          found.push(d);
          remaining.delete(d.id);
        }
      });
      if (!r.data || !r.data.has_more) break;
      cursor = r.data.next_cursor;
      safety -= 1;
    }
    return found;
  };

  // ===== Existing gallery CRUD (unchanged behavior) =====
  const add = async () => {
    if (!newImage) return;
    await api.post('/admin/gallery', { image: newImage, visible: true });
    setNewImage(''); setAdding(false); load();
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      load();
    } catch (e) {
      // 409 write-barrier — surface the server's clear message.
      const msg = (e && e.response && e.response.data && e.response.data.detail) || 'Delete failed.';
      window.alert(msg);
    }
  };
  const move = async (id, dir) => {
    const idx = items.findIndex((i) => i.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const next = [...items];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    await api.put('/admin/gallery/reorder', { items: next.map((it, i) => ({ id: it.id, order: i })) });
  };

  // ===== Round 3 — Homepage picker =====
  const openPickerFor = async (slotIdx) => {
    setPickerSlot(slotIdx);
    setPickerOpen(true);
    setPickerSearch('');
    setPickerItems([]);
    setPickerCursor(null);
    setPickerHasMore(false);
    await loadPickerPage(null, '');
  };
  const closePicker = () => {
    setPickerOpen(false); setPickerSlot(null); setPickerItems([]); setPickerCursor(null); setPickerSearch('');
  };
  const loadPickerPage = async (cursor, search) => {
    setPickerLoading(true);
    try {
      const params = { limit: 50 };
      if (cursor) params.cursor = cursor;
      if (search) params.search = search;
      const r = await api.get('/admin/gallery/picker', { params });
      const list = (r.data && r.data.items) || [];
      setPickerItems((prev) => cursor ? [...prev, ...list] : list);
      setPickerCursor((r.data && r.data.next_cursor) || null);
      setPickerHasMore(!!(r.data && r.data.has_more));
    } finally {
      setPickerLoading(false);
    }
  };
  const runPickerSearch = async () => {
    await loadPickerPage(null, pickerSearch);
  };

  const chooseFromPicker = (photo) => {
    if (pickerSlot == null) return;
    // Duplicate prevention: reject if already in another slot.
    if (slotIds.some((sid, i) => sid === photo.id && i !== pickerSlot)) {
      window.alert('This photo is already selected in another slot. Remove it from that slot first, or pick a different photo.');
      return;
    }
    const next = [...slotIds];
    next[pickerSlot] = photo.id;
    setSlotIds(next);
    setSlotPhotos({ ...slotPhotos, [photo.id]: photo });
    closePicker();
  };
  const clearSlot = (slotIdx) => {
    const next = [...slotIds];
    next[slotIdx] = null;
    setSlotIds(next);
  };
  const moveSlot = (slotIdx, dir) => {
    const target = slotIdx + dir;
    if (target < 0 || target >= 6) return;
    const next = [...slotIds];
    [next[slotIdx], next[target]] = [next[target], next[slotIdx]];
    setSlotIds(next);
  };

  const selectedCount = slotIds.filter(Boolean).length;
  const hasDuplicates = useMemo(() => {
    const nonNull = slotIds.filter(Boolean);
    return new Set(nonNull).size !== nonNull.length;
  }, [slotIds]);
  const canSave = selectedCount === 6 && !hasDuplicates && !savingSelection;

  const saveSelection = async () => {
    if (!canSave) return;
    setSavingSelection(true);
    setSaveMsg(null); setSaveErr(null);
    try {
      await api.put('/admin/settings/homepage-gallery', { ids: slotIds });
      setSaveMsg('Homepage selection saved.');
      setTimeout(() => setSaveMsg(null), 3000);
      await load();
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || 'Save failed. Please try again.';
      setSaveErr(String(detail));
    } finally {
      setSavingSelection(false);
    }
  };

  // ===== Admin Recovery actions =====
  // Manual status check. Never called on tab mount to avoid any
  // implicit "auto-detect stale" behavior.
  const checkPendingOperation = async () => {
    setRecoveryLoading(true);
    setRecoveryErr(null);
    setTakeoverErr(null);
    setTakeoverMsg(null);
    try {
      const r = await api.get('/admin/gallery/operation-status');
      setRecoveryStatus(r.data || null);
      // If there is no pending op, reset the confirmation field so the
      // admin cannot carry a stale phrase across checks.
      if (!(r.data && r.data.has_pending)) {
        setTakeoverConfirmText('');
      }
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || 'Failed to fetch operation status.';
      setRecoveryErr(String(detail));
    } finally {
      setRecoveryLoading(false);
    }
  };

  const performTakeover = async () => {
    if (!takeoverCanSubmit) return;
    const expected = recoveryStatus && recoveryStatus.pending_operation && recoveryStatus.pending_operation.operation_id;
    if (!expected) return;
    setTakeoverBusy(true);
    setTakeoverErr(null);
    setTakeoverMsg(null);
    try {
      const r = await api.post('/admin/gallery/takeover-operation', { expected_operation_id: expected });
      const took = (r.data && r.data.took_over_from) || expected;
      setTakeoverMsg(`Takeover complete. Cleared operation ${took}. You may now retry your save, hide, or delete explicitly.`);
      setTakeoverConfirmText('');
      // Refresh status per the recovery contract; do NOT auto-retry any
      // mutation the admin was previously attempting.
      await checkPendingOperation();
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || 'Takeover failed.';
      setTakeoverErr(String(detail));
    } finally {
      setTakeoverBusy(false);
    }
  };

  return (
    <div>
      {/* Round 3 — Homepage Gallery selection */}
      <Section title="Homepage Gallery — “Built For What's Next” Tiles">
        <p className="font-body text-[13px] text-ink-muted mb-3" data-testid="homepage-gallery-hint">
          Select exactly six photos, in the order they should appear on the homepage tiles. These stay pinned even if you upload new photos or reorder the full gallery.
        </p>
        {!hg.configured && (
          <div className="mb-4 p-3 border border-brand-orange/40 bg-brand-orange/10 rounded-sm" data-testid="homepage-gallery-unconfigured-notice">
            <p className="font-body text-[12px] text-ink-primary">
              <strong>Not yet configured.</strong> The homepage is currently showing the first six visible gallery photos by <em>order</em>. Choose six below and Save to lock the selection.
            </p>
          </div>
        )}
        {/* Admin Recovery — manual. No auto-poll, no auto-takeover. */}
        <div className="mb-4 p-3 border border-border-line bg-bg-canvas rounded-sm" data-testid="homepage-gallery-recovery-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2 min-w-0">
              <ShieldAlert size={16} className="text-ink-muted mt-[2px] shrink-0" />
              <div className="min-w-0">
                <p className="font-body text-[12px] text-ink-primary">
                  <strong>Recovery</strong> — If a save, hide, or delete appears blocked, check here for a pending operation.
                </p>
                <p className="font-body text-[11px] text-ink-muted mt-0.5">
                  This tool does not detect stale operations for you; it shows the current state. Use takeover only when the responsible admin is confirmed offline.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={checkPendingOperation}
              disabled={recoveryLoading}
              className="btn-secondary text-[11px] !py-1.5 !px-3 shrink-0"
              data-testid="homepage-recovery-check"
            >{recoveryLoading ? 'Checking…' : 'Check status'}</button>
          </div>
          {recoveryErr && (
            <p className="mt-2 font-body text-[11px] text-red-700" data-testid="homepage-recovery-error">{recoveryErr}</p>
          )}
          {recoveryStatus && !recoveryStatus.has_pending && (
            <p className="mt-2 font-body text-[11px] text-green-700" data-testid="homepage-recovery-idle">
              No pending operation. Current generation: {recoveryStatus.generation}.
            </p>
          )}
          {recoveryStatus && recoveryStatus.has_pending && recoveryStatus.pending_operation && (
            <div className="mt-3 border border-brand-orange/40 bg-brand-orange/5 rounded-sm p-3" data-testid="homepage-recovery-pending-block">
              <p className="font-body text-[12px] text-ink-primary font-bold uppercase tracking-wider2">Pending operation detected</p>
              <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 font-body text-[11px] text-ink-primary">
                <div className="flex gap-2"><dt className="text-ink-muted min-w-[110px]">Operation ID</dt><dd className="break-all" data-testid="homepage-recovery-op-id">{recoveryStatus.pending_operation.operation_id}</dd></div>
                <div className="flex gap-2"><dt className="text-ink-muted min-w-[110px]">Type</dt><dd data-testid="homepage-recovery-op-type">{recoveryStatus.pending_operation.type || '—'}</dd></div>
                <div className="flex gap-2"><dt className="text-ink-muted min-w-[110px]">Phase</dt><dd data-testid="homepage-recovery-op-phase">{recoveryStatus.pending_operation.phase || '—'}</dd></div>
                <div className="flex gap-2"><dt className="text-ink-muted min-w-[110px]">Generation</dt><dd data-testid="homepage-recovery-op-gen">{recoveryStatus.pending_operation.generation}</dd></div>
                <div className="flex gap-2"><dt className="text-ink-muted min-w-[110px]">Started at</dt><dd data-testid="homepage-recovery-op-started">{recoveryStatus.pending_operation.started_at || '—'}</dd></div>
                {recoveryStatus.pending_operation.target_id && (
                  <div className="flex gap-2"><dt className="text-ink-muted min-w-[110px]">Target ID</dt><dd className="break-all" data-testid="homepage-recovery-op-target-id">{recoveryStatus.pending_operation.target_id}</dd></div>
                )}
                {Array.isArray(recoveryStatus.pending_operation.target_ids) && recoveryStatus.pending_operation.target_ids.length > 0 && (
                  <div className="flex gap-2 sm:col-span-2"><dt className="text-ink-muted min-w-[110px]">Target IDs</dt><dd className="break-all" data-testid="homepage-recovery-op-target-ids">{recoveryStatus.pending_operation.target_ids.join(', ')}</dd></div>
                )}
                {recoveryStatus.pending_operation.took_over_from && (
                  <div className="flex gap-2 sm:col-span-2"><dt className="text-ink-muted min-w-[110px]">Took over from</dt><dd className="break-all" data-testid="homepage-recovery-op-took-over">{recoveryStatus.pending_operation.took_over_from}</dd></div>
                )}
              </dl>
              <div className="mt-3 border-t border-border-line pt-3">
                <p className="font-body text-[11px] text-ink-primary">
                  To force-release this operation and clear the block, type <code className="font-mono bg-white border border-border-line px-1 py-0.5 text-[10px]">{TAKEOVER_CONFIRM_PHRASE}</code> and click <strong>Take over</strong>. This does not retry your action; retry it explicitly after the block is cleared.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={takeoverConfirmText}
                    onChange={(e) => setTakeoverConfirmText(e.target.value)}
                    placeholder={`Type "${TAKEOVER_CONFIRM_PHRASE}"`}
                    className="border border-border-line rounded-sm px-3 py-1.5 font-body text-[12px] bg-white focus:outline-none focus:border-brand-orange"
                    data-testid="homepage-recovery-confirm-input"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    onClick={performTakeover}
                    disabled={!takeoverCanSubmit}
                    className="btn-primary text-[11px] !py-1.5 !px-3 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="homepage-recovery-takeover"
                  >{takeoverBusy ? 'Taking over…' : 'Take over'}</button>
                </div>
              </div>
            </div>
          )}
          {takeoverErr && (
            <p className="mt-2 font-body text-[11px] text-red-700" data-testid="homepage-recovery-takeover-error">{takeoverErr}</p>
          )}
          {takeoverMsg && (
            <p className="mt-2 font-body text-[11px] text-green-700" data-testid="homepage-recovery-takeover-success">{takeoverMsg}</p>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-3">
          {slotIds.map((sid, idx) => {
            const photo = lookupPhoto(sid);
            const isUnavailable = sid && !photo;
            return (
              <div key={idx} className="border border-border-line rounded-sm overflow-hidden bg-white flex flex-col" data-testid={`homepage-slot-${idx}`}>
                <div className="relative aspect-[4/3] bg-bg-canvas flex items-center justify-center">
                  {photo && photo.image ? (
                    <img src={fileUrl(photo.image)} alt={photo.caption || `Slot ${idx + 1}`} className="w-full h-full object-cover" />
                  ) : isUnavailable ? (
                    <div className="text-center px-2">
                      <p className="font-body text-[11px] text-red-700 uppercase tracking-wider2">Unavailable</p>
                      <p className="font-body text-[10px] text-ink-muted mt-1">Replace this slot to keep six.</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="font-body text-[11px] text-ink-soft uppercase tracking-wider2">Slot {idx + 1}</p>
                      <p className="font-body text-[10px] text-ink-muted">Empty</p>
                    </div>
                  )}
                  <span className="absolute top-1 left-1 bg-black/60 text-white font-body text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider2">{idx + 1}</span>
                </div>
                <div className="flex items-center justify-between p-2 gap-1">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveSlot(idx, -1)}
                      disabled={idx === 0}
                      className="p-1 disabled:opacity-30"
                      data-testid={`homepage-slot-${idx}-up`}
                    ><ChevronUp size={12} /></button>
                    <button
                      type="button"
                      onClick={() => moveSlot(idx, 1)}
                      disabled={idx === 5}
                      className="p-1 disabled:opacity-30"
                      data-testid={`homepage-slot-${idx}-down`}
                    ><ChevronDown size={12} /></button>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openPickerFor(idx)}
                      className="btn-secondary !py-1 !px-2 text-[10px]"
                      data-testid={`homepage-slot-${idx}-choose`}
                    >{sid ? 'Replace' : 'Choose'}</button>
                    {sid && (
                      <button
                        type="button"
                        onClick={() => clearSlot(idx)}
                        className="btn-secondary !py-1 !px-2 text-[10px]"
                        data-testid={`homepage-slot-${idx}-clear`}
                      ><X size={12} /></button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-body text-[12px] text-ink-muted" data-testid="homepage-gallery-counter">
            {selectedCount} of 6 selected{hasDuplicates ? ' — duplicate detected' : ''}
          </span>
          <button
            type="button"
            onClick={saveSelection}
            disabled={!canSave}
            className="btn-primary text-[12px] !py-2 !px-4 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="homepage-gallery-save"
          >{savingSelection ? 'Saving…' : (hg.configured ? 'Update Selection' : 'Save Selection')}</button>
          {saveMsg && <span className="font-body text-[12px] text-green-700" data-testid="homepage-gallery-save-ok">{saveMsg}</span>}
          {saveErr && <span className="font-body text-[12px] text-red-700" data-testid="homepage-gallery-save-error">{saveErr}</span>}
        </div>
      </Section>

      {/* Existing gallery admin (unchanged behavior) */}
      <Section title="Gallery (Past Experiences)" action={<button onClick={() => setAdding(true)} className="btn-primary text-[12px] !py-2.5 !px-4" data-testid="add-gallery"><Plus size={14} className="mr-1" /> Add Image</button>}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {items.map((it, idx) => (
            <div key={it.id} className="border border-border-line rounded-sm overflow-hidden bg-white">
              <div className="aspect-[4/3] bg-bg-canvas">
                {it.image && <img src={fileUrl(it.image)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex items-center justify-between p-2">
                <div className="flex items-center gap-1">
                  <button onClick={() => move(it.id, -1)} disabled={idx === 0} className="p-1 disabled:opacity-30"><ChevronUp size={14} /></button>
                  <button onClick={() => move(it.id, 1)} disabled={idx === items.length - 1} className="p-1 disabled:opacity-30"><ChevronDown size={14} /></button>
                </div>
                <button onClick={() => remove(it.id)} className="btn-secondary !py-1.5 !px-2 text-[11px]"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {adding && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setAdding(false)}>
          <div className="bg-bg-elevated max-w-lg w-full rounded-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold uppercase text-[18px] mb-4">Add Gallery Image</h3>
            <ImageUploader value={newImage} onChange={setNewImage} label="Image" />
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => { setAdding(false); setNewImage(''); }} className="btn-secondary text-[12px] !py-2 !px-4">Cancel</button>
              <button onClick={add} disabled={!newImage} className="btn-primary text-[12px] !py-2 !px-4">Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Round 3 — Homepage-picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={closePicker}>
          <div className="bg-bg-elevated max-w-4xl w-full rounded-lg p-6 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold uppercase text-[18px]">Choose Photo for Slot {pickerSlot != null ? pickerSlot + 1 : ''}</h3>
              <button onClick={closePicker} className="btn-secondary !py-1.5 !px-2 text-[11px]" data-testid="homepage-picker-close"><X size={14} /></button>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-2 flex-1 border border-border-line rounded-sm px-3 py-2 bg-white">
                <Search size={14} className="text-ink-soft" />
                <input
                  type="text"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') runPickerSearch(); }}
                  placeholder="Search caption…"
                  className="flex-1 font-body text-[13px] bg-transparent focus:outline-none"
                  data-testid="homepage-picker-search"
                />
                {pickerSearch && (
                  <button onClick={() => { setPickerSearch(''); loadPickerPage(null, ''); }} className="text-ink-soft"><X size={14} /></button>
                )}
              </div>
              <button onClick={runPickerSearch} className="btn-secondary text-[12px] !py-2 !px-4" data-testid="homepage-picker-search-btn">Search</button>
            </div>
            <div className="flex-1 overflow-y-auto -mx-2 px-2">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="homepage-picker-grid">
                {pickerItems.map((p) => {
                  const inSlot = slotIds.indexOf(p.id);
                  const isSelected = inSlot >= 0;
                  const isThisSlot = inSlot === pickerSlot;
                  const isHidden = p.visible === false;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => chooseFromPicker(p)}
                      className={`relative border-2 rounded-sm overflow-hidden bg-white text-left ${isThisSlot ? 'border-brand-orange' : (isSelected ? 'border-ink-muted' : 'border-border-line')} hover:border-brand-orange transition`}
                      data-testid={`homepage-picker-item-${p.id}`}
                      title={p.caption || ''}
                    >
                      <div className="aspect-[4/3] bg-bg-canvas relative">
                        {p.image && <img src={fileUrl(p.image)} alt={p.caption || ''} className="w-full h-full object-cover" />}
                        {isHidden && (
                          <span className="absolute top-1 left-1 bg-red-700 text-white font-body text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider2">Hidden</span>
                        )}
                        {isSelected && (
                          <span className="absolute top-1 right-1 bg-brand-orange text-white font-body text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider2">Slot {inSlot + 1}</span>
                        )}
                      </div>
                      {p.caption && (
                        <div className="p-2 font-body text-[11px] text-ink-primary line-clamp-2">{p.caption}</div>
                      )}
                    </button>
                  );
                })}
                {pickerItems.length === 0 && !pickerLoading && (
                  <p className="col-span-full font-body text-[12px] text-ink-muted text-center py-8">No photos match. Try clearing the search.</p>
                )}
              </div>
              {pickerHasMore && (
                <div className="text-center py-4">
                  <button
                    type="button"
                    onClick={() => loadPickerPage(pickerCursor, pickerSearch)}
                    disabled={pickerLoading}
                    className="btn-secondary text-[12px] !py-2 !px-4"
                    data-testid="homepage-picker-load-more"
                  >{pickerLoading ? 'Loading…' : 'Load more'}</button>
                </div>
              )}
              {pickerLoading && pickerItems.length === 0 && (
                <p className="text-center font-body text-[12px] text-ink-muted py-8">Loading…</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
